import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Empty, Modal, Space, Tooltip } from "antd";
import cloud from "d3-cloud";
import { schemeCategory10 } from "d3-scale-chromatic";
import { event as d3Event, select } from "d3-selection";

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "Benchmarking/Scss/KeywordCloudD3.scss";
import TimePicker from "./TimePicker";

const breadCrumbFontSize = "21px";

function getConceptLink(id) {
  if (id == null || id == undefined) {
    return "";
  }

  return "/explore/concept/" + id.toString();
}

const ContextMenuRightClick = ({
  rightClickedOnKeyword,
  setIsModalOpen,
  popUpPoints,
  idOfClickedOnKeyword,
}) => {
  const conceptLink = getConceptLink(idOfClickedOnKeyword);
  const navigate = useNavigate();

  return (
    <>
      {rightClickedOnKeyword && (
        <>
          <div
            className="rightclick-context-menu-keyword-cloud"
            onClick={() => {
              setIsModalOpen(true);
            }}
            style={{
              top: popUpPoints.y,
              left: popUpPoints.x,
              borderRadius: "4px 4px 0px 0px",
            }}
          >
            <ul>
              <li>Show definition</li>
            </ul>
          </div>

          <div
            className="rightclick-context-menu-keyword-cloud"
            onClick={() => {
              navigate(conceptLink);
              window.location.reload();
            }}
            style={{
              top: popUpPoints.y + 28,
              left: popUpPoints.x,
              borderRadius: "0px 0px 4px 4px",
            }}
          >
            <ul>
              <li>To concept page</li>
            </ul>
          </div>
        </>
      )}
    </>
  );
};

const DefinitionModal = ({
  isModalOpen,
  setIsModalOpen,
  conceptToDisplay,
  definitionToDisplay,
}) => {
  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      <Modal
        bodyStyle={{ maxHeight: "250px", overflowY: "scroll" }}
        title={conceptToDisplay}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>{definitionToDisplay}</p>
      </Modal>
    </>
  );
};

const GranularityZoomButtons = ({
  keywords,
  granularityLevel,
  setGranularityLevel,
  selectedKeywords,
  setSelectedKeywords,
  selectedKeywordsQueue,
  setSelectedKeywordsQueue,
  isLoading,
}) => {
  const maxValue = 4;
  const minValue = 1;
  const step = 1;

  const isAtMax = granularityLevel === maxValue;
  const isAtMin = granularityLevel === minValue;
  const isNoKeywordLeft =
    keywords.filter((item) => {
      if (selectedKeywordsQueue[selectedKeywordsQueue.length - 2]) {
        return !selectedKeywordsQueue[selectedKeywordsQueue.length - 2]
          .map((kw) => kw.text)
          .includes(item.value);
      } else {
        return true;
      }
    }).length === 0;

  const temporaryDisableButton = () => {};

  const handleIncrease = () => {
    setTimeout(() => {
      if (granularityLevel < maxValue) {
        setGranularityLevel(granularityLevel + step);
      }
    }, 100);

    temporaryDisableButton();

    setSelectedKeywordsQueue([...selectedKeywordsQueue, selectedKeywords]);
    setSelectedKeywords([]);
  };

  const handleDecrease = () => {
    setTimeout(() => {
      if (granularityLevel > minValue) {
        setGranularityLevel(granularityLevel - step);
      }
    }, 100);

    temporaryDisableButton();

    setSelectedKeywords(
      selectedKeywordsQueue[selectedKeywordsQueue.length - 1]
    );
    setSelectedKeywordsQueue(selectedKeywordsQueue.slice(0, -1));
  };

  return (
    <div style={{ width: "fit-content" }}>
      <Space.Compact block>
        <Tooltip title="Zoom In">
          <Button //style={{ background: mainColor, color: "white" }}
            disabled={isAtMax || isLoading || isNoKeywordLeft} // {isAtMax || isNoKeywordLeft || !buttonClickable || isZoomInTransition || isZoomOutTransition}
            onClick={handleIncrease}
            icon={<PlusOutlined />}
          />
        </Tooltip>
        <Tooltip title="Zoom Out">
          <Button //style={{ background: mainColor, color: "white" }}
            disabled={isAtMin || isLoading} //{isAtMin || !buttonClickable || isZoomOutTransition || isZoomInTransition}
            onClick={handleDecrease}
            icon={<MinusOutlined />}
          />
        </Tooltip>
      </Space.Compact>
    </div>
  );
};

const TrackingBreadCrumb = ({ selectedKeywordsQueue }) => {
  const commonTextStyle = {}; //{ "fontSize": breadCrumbFontSize, "height": "fit-content", "width": "fit-content" };
  const multiTextStyle = {}; //{"fontSize": breadCrumbFontSize}

  const itemsToDisplay = selectedKeywordsQueue.map(function (line) {
    if (line.length === 1) {
      return {
        title: <a style={commonTextStyle}>{line[0].value}</a>,
      };
    } else if (line.length > 1) {
      const lineMenuItems = line.slice(1).map((subline, index) => {
        return {
          key: `${index}`,
          label: <a style={commonTextStyle}>{subline.value}</a>,
        };
      });
      return {
        title: <a style={commonTextStyle}>{line[0].value}</a>,
        menu: {
          items: lineMenuItems,
        },
      };
    } else {
      return {
        title: <a style={commonTextStyle}></a>,
      };
    }
  });

  return (
    <Breadcrumb
      style={{ height: breadCrumbFontSize }}
      separator={<a style={commonTextStyle}>{">"}</a>}
      items={itemsToDisplay}
    />
  );
};

const KeywordCloud = ({
  keywords,
  selectedKeywords,
  setSelectedKeywords,
  granularityLevel,
  setGranularityLevel,
  selectedKeywordsQueue,
  setSelectedKeywordsQueue,
  sliderValue,
  setSliderValue,
  bigCloud = true,
}) => {
  const width = bigCloud ? 1200 : 800;
  const height = bigCloud ? 600 : 300;
  const padding = bigCloud ? 6.5 : 5;

  const elementRef = useRef();
  const [rightClickedOnKeyword, setRightClickedOnKeyword] = useState(false);
  const [idOfClickedOnKeyword, setIdOfClickedOnKeyword] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popUpPoints, setPopUpPoints] = useState({
    x: 0,
    y: 0,
  });
  const [conceptToDisplay, setConceptToDisplay] = useState("");
  const [definitionToDisplay, setDefinitionToDisplay] = useState("");
  const [data, setData] = useState([]);
  const [oldData, setOldData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const normalizeValueToNewRange = (
    oldMin,
    oldMax,
    newMin,
    newMax,
    valueToNorm
  ) => {
    const oldRange = oldMax - oldMin;
    const newRange = newMax - newMin;

    const normalizedValue = Math.round(
      ((valueToNorm - oldMin) * newRange) / oldRange + newMin
    );

    if (normalizedValue >= newMin && normalizedValue <= newMax) {
      return normalizedValue;
    } else if (normalizedValue > newMax) {
      return newMax;
    } else if (normalizedValue < newMin) {
      return newMin;
    } else {
      return newMin;
    }
  };

  const preprocessInputData = (keywords) => {
    var minScoreValue = Math.min(...keywords.map((item) => item.score));
    var maxScoreValue = Math.max(...keywords.map((item) => item.score));

    // the keywords should be sorted based on their score
    var preprocessedData = keywords.map((item, index) => ({
      id: item.id,
      real_id: item.id,
      int_id: item.int_id,
      text: item.value,
      value: item.value,
      size: normalizeValueToNewRange(
        minScoreValue,
        maxScoreValue,
        15,
        40,
        item.score
      ),
      // "score": item.score,
      index: index,
      definition: item.definition,
    }));
    return preprocessedData;
  };

  useEffect(() => {
    setIsLoading(true);
  }, [granularityLevel, sliderValue]);

  useEffect(() => {
    const handleClick = () => {
      setRightClickedOnKeyword(false);
      setIdOfClickedOnKeyword();
    };
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [keywords]);

  useEffect(() => {
    var preprocessedData = preprocessInputData(keywords);
    setOldData([]);
    setOldData(data);

    setData([]);
    setData(preprocessedData);

    setIsLoading(false);
  }, [keywords]);

  useEffect(() => {
    if (selectedKeywords) {
      if (
        granularityLevel >= 2 &&
        selectedKeywordsQueue[granularityLevel - 2]
      ) {
        setOldData(data);
        setData((prevData) =>
          prevData.filter((line) => {
            return selectedKeywordsQueue[granularityLevel - 2]
              .map((item) => item.value)
              .includes(line.text);
          })
        );
      } else if (granularityLevel == 1) {
      } else {
        setOldData([]);
        setData([]);
      }
    } else {
      setOldData([]);
      setData([]);
    }
  }, [granularityLevel]);

  useEffect(() => {
    const el = elementRef.current;
    const svg = select(el);

    const layout = cloud()
      .size([width, height])
      .words(data)
      .padding(padding)
      .rotate(() => 0)
      .font("Roboto")
      .fontSize((d) => d.size)
      .random(() => 0.5)
      // .padding(6.5)
      .fontWeight("bold")
      .on("end", (words) => {
        const g = svg.selectAll("g").data([null]);

        // Enter new G element
        g.enter()
          .append("g")
          .attr("transform", `translate(${width / 2},${height / 2})`)
          .merge(g) // Merge enter and update selections
          .selectAll("text")
          .data(words, (d) => d.text)
          .join(
            (enter) =>
              enter
                .append("text")
                .attr("text-anchor", "middle")
                .attr(
                  "transform",
                  (d) => `translate(${d.x},${d.y}) rotate(${d.rotate})`
                )
                .style("font-size", (d) => `${d.size}px`)
                .style(
                  "fill",
                  (d, i, n) => schemeCategory10[n[i].__data__.int_id % 10]
                )
                .style("opacity", 0)
                .text((d) => d.text)
                .call((selection) =>
                  selection.transition().duration(500).style("opacity", 1)
                )
                .each(function (_, i, n) {
                  if (
                    selectedKeywords
                      .map((sk) => sk.value)
                      .includes(n[i].__data__.text)
                  ) {
                    // if (selectedKeywords.includes(n[i].__data__.text)) {
                    const bbox = _;

                    select(n[i].parentNode)
                      .select(
                        `rect.keyword-background-${n[i].__data__.real_id}`
                      )
                      .transition()
                      .duration(150)
                      .style("opacity", 0)
                      .remove();

                    select(n[i].parentNode)
                      .insert("rect", ":first-child")
                      .transition()
                      .delay(500)
                      .attr(
                        "class",
                        `keyword-background-${n[i].__data__.real_id}`
                      )
                      .style("fill", "lightgray")
                      .attr("rx", "6px") // Set horizontal border radius
                      // .attr("ry", "10%")
                      .attr("x", bbox.x - (bbox.width * 1.8) / 2)
                      .attr("y", bbox.y - (bbox.height * 1.8) / 2)
                      .attr("width", bbox.width * 1.8)
                      .attr("height", `${n[i].__data__.size * 1.5 * 1.8}px`)
                      .transition()
                      .duration(500)
                      .attr("x", bbox.x - bbox.width / 2)
                      .attr("y", bbox.y - bbox.height / 2)
                      .attr("width", bbox.width)
                      .attr("height", `${n[i].__data__.size * 1.5}px`);
                  }
                })
                .on("mouseover", (_, i, n) => {
                  const bbox = _;

                  select(n[i])
                    .style("cursor", "pointer")
                    .transition()
                    .duration(150)
                    .style("font-size", (d) => `${d.size * 1.5}px`);

                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .transition()
                    .duration(150)
                    .attr("x", bbox.x - (bbox.width * 1.5) / 2)
                    .attr("y", bbox.y - (bbox.height * 1.5) / 2)
                    .attr("width", bbox.width * 1.5)
                    .attr("height", `${n[i].__data__.size * 1.5 * 1.5}px`);
                })
                .on("mouseout", (_, i, n) => {
                  // Handle mouseout event
                  // Example: Restore font size on mouseout

                  const bbox = _;

                  select(n[i])
                    .style("cursor", "default")
                    .transition()
                    .duration(150)
                    .style("font-size", (d) => `${d.size}px`);

                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .transition()
                    .duration(150)
                    .attr("x", bbox.x - bbox.width / 2)
                    .attr("y", bbox.y - bbox.height / 2)
                    .attr("width", bbox.width)
                    .attr("height", `${n[i].__data__.size * 1.5}px`);
                })
                .on("contextmenu", (_, i, n) => {
                  d3Event.preventDefault();

                  // set definition for display
                  setDefinitionToDisplay(n[i].__data__.definition);
                  setConceptToDisplay(n[i].__data__.text);

                  setPopUpPoints({
                    x: d3Event.pageX,
                    y: d3Event.pageY,
                  });

                  if (rightClickedOnKeyword) {
                    setRightClickedOnKeyword(false);
                    setIdOfClickedOnKeyword();
                  } else {
                    setRightClickedOnKeyword(true);
                    setIdOfClickedOnKeyword(n[i].__data__.real_id);
                  }
                })
                .on("click", (_, i, n) => {
                  const bbox = _;
                  setSelectedKeywords((prevSelected) => {
                    // Check if the keyword is already selected
                    const isSelected = prevSelected
                      .map((item) => item.value)
                      .includes(n[i].__data__.text);

                    // Toggle selection based on current state
                    if (isSelected) {
                      // console.log("yo remove clicked")
                      // If already selected, remove it from the selectedKeywords array
                      select(n[i].parentNode)
                        .select(
                          `rect.keyword-background-${n[i].__data__.real_id}`
                        )
                        .transition()
                        .duration(150)
                        .style("opacity", 0)
                        .remove();
                      // return prevSelected.filter(keyword => keyword.value !== n[i].__data__.text);
                      return prevSelected.filter(
                        (keyword) => keyword.value !== n[i].__data__.text
                      );
                    } else {
                      // console.log("yo init clicked")
                      // If not selected, add it to the selectedKeywords array
                      select(n[i].parentNode)
                        .insert("rect", ":first-child")
                        .attr(
                          "class",
                          `keyword-background-${n[i].__data__.real_id}`
                        )
                        .style("fill", "lightgray")
                        .attr("rx", "6px") // Set horizontal border radius
                        // .attr("ry", "10%")
                        .attr("x", bbox.x - (bbox.width * 1.8) / 2)
                        .attr("y", bbox.y - (bbox.height * 1.8) / 2)
                        .attr("width", bbox.width * 1.8)
                        .attr("height", `${n[i].__data__.size * 1.5 * 1.8}px`)
                        .transition()
                        .duration(500)
                        .attr("x", bbox.x - (bbox.width * 1.5) / 2)
                        .attr("y", bbox.y - (bbox.height * 1.5) / 2)
                        .attr("width", bbox.width * 1.5)
                        .attr("height", `${n[i].__data__.size * 1.5 * 1.5}px`);
                      return [
                        ...prevSelected,
                        {
                          real_id: n[i].__data__.id,
                          value: n[i].__data__.text,
                          id: n[i].__data__.text,
                          text: n[i].__data__.text,
                        },
                      ];
                      // return [...prevSelected, n[i].__data__.text];
                    }
                  });
                }),
            (update) =>
              update
                .transition()
                .duration(500)
                .attr(
                  "transform",
                  (d) => `translate(${d.x},${d.y}) rotate(${d.rotate})`
                )
                .style("font-size", (d) => `${d.size}px`)
                .style(
                  "fill",
                  (d, i, n) => schemeCategory10[n[i].__data__.int_id % 10]
                )
                .style("opacity", 1)
                .each(function (_, i, n) {
                  if (
                    selectedKeywords
                      .map((item) => item.value)
                      .includes(n[i].__data__.text)
                  ) {
                    // if (selectedKeywords.includes(n[i].__data__.text)) {
                    const bbox = _;

                    select(n[i].parentNode)
                      .select(
                        `rect.keyword-background-${n[i].__data__.real_id}`
                      )
                      .style("opacity", 0)
                      .remove();

                    select(n[i].parentNode)
                      .insert("rect", ":first-child")
                      .transition()
                      .delay(500)
                      .attr(
                        "class",
                        `keyword-background-${n[i].__data__.real_id}`
                      )
                      .style("fill", "lightgray")
                      .attr("rx", "6px") // Set horizontal border radius
                      // .attr("ry", "10%")
                      .attr("x", bbox.x - (bbox.width * 1.8) / 2)
                      .attr("y", bbox.y - (bbox.height * 1.8) / 2)
                      .attr("width", bbox.width * 1.8)
                      .attr("height", `${n[i].__data__.size * 1.5 * 1.8}px`)
                      .transition()
                      .duration(500)
                      .attr("x", bbox.x - bbox.width / 2)
                      .attr("y", bbox.y - bbox.height / 2)
                      .attr("width", bbox.width)
                      .attr("height", `${n[i].__data__.size * 1.5}px`);
                  } else if (
                    selectedKeywordsQueue &&
                    selectedKeywordsQueue[selectedKeywordsQueue.length - 1] &&
                    selectedKeywordsQueue[selectedKeywordsQueue.length - 1]
                      .map((item) => item.value)
                      .includes(n[i].__data__.text)
                  ) {
                    select(n[i].parentNode)
                      .select(
                        `rect.keyword-background-${n[i].__data__.real_id}`
                      )
                      .style("opacity", 0)
                      .remove();
                  }
                }),
            (exit) =>
              exit
                .each(function (_, i, n) {
                  // // console.log(i, `rect.keyword-background-${oldn[i].__data__.text.replaceAll(" ", "--")}`)
                  // console.log("exit", n);
                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .style("opacity", 0)
                    .remove();
                })
                .transition()
                .duration(500)
                .style("opacity", 0)
                .remove()
          );
      });

    layout.start();
  }, [data]);

  return (
    <div className="keyword-cloud-container">
      <div className="operations-tab-outer">
        <div className="operations-tab">
          <GranularityZoomButtons
            keywords={data}
            granularityLevel={granularityLevel}
            setGranularityLevel={setGranularityLevel}
            selectedKeywords={selectedKeywords}
            setSelectedKeywords={setSelectedKeywords}
            selectedKeywordsQueue={selectedKeywordsQueue}
            setSelectedKeywordsQueue={setSelectedKeywordsQueue}
            isLoading={isLoading}
          />

          <TrackingBreadCrumb selectedKeywordsQueue={selectedKeywordsQueue} />

          <TimePicker
            value={sliderValue}
            setValue={setSliderValue}
            selectedKeywords={selectedKeywords}
            setSelectedKeywords={setSelectedKeywords}
            disabled={isLoading}
          />
        </div>
      </div>

      <svg ref={elementRef} width={`${width}`} height={`${height}`} />

      <ContextMenuRightClick
        rightClickedOnKeyword={rightClickedOnKeyword}
        setIsModalOpen={setIsModalOpen}
        popUpPoints={popUpPoints}
        idOfClickedOnKeyword={idOfClickedOnKeyword}
      />

      <DefinitionModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        conceptToDisplay={conceptToDisplay}
        definitionToDisplay={definitionToDisplay}
      />
    </div>
  );
};

export default KeywordCloud;
